'use client';

import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { openTrackedWhatsAppHref } from '@/lib/whatsapp/open-tracked-whatsapp';
import WhatsAppIntakeSheet, {
  type WhatsAppIntakeContext,
} from '@/components/whatsapp/WhatsAppIntakeSheet';
import WhatsAppGlyph from '@/components/whatsapp/WhatsAppGlyph';

interface StickyMobileCTAProps {
  /** Anchor al grid de unidades. Default: "#unidades" */
  primaryHref?: string;
  primaryText?: string;
  whatsappLink?: string;
  whatsappText?: string;
  /** Posición telemétrica del CTA WhatsApp (default: "sticky_mobile") */
  whatsappPosition?: string;
  /** Si viene, el CTA WhatsApp abre el asistente de fechas (W2) en vez de wa.me. */
  intake?: Omit<WhatsAppIntakeContext, 'ctaPosition'>;
}

/**
 * CTA flotante visible solo en mobile (< md). Resuelve la fatiga de scroll:
 * el usuario tiene siempre acción primaria + WhatsApp sin tener que volver al hero.
 *
 * Pasó a Client Component (era pure CSS) para emitir `cta_click_whatsapp` custom
 * con `position: sticky_mobile`. Autocapture es inconsistente con buttons SVG sin texto
 * — el evento custom da atribución 1:1.
 */
export default function StickyMobileCTA({
  primaryHref = '#unidades',
  primaryText = 'Ver disponibilidad',
  whatsappLink,
  whatsappText = 'WhatsApp',
  whatsappPosition = 'sticky_mobile',
  intake,
}: StickyMobileCTAProps) {
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const targets = Array.from(document.querySelectorAll('.pa-hero, #consultar-disponibilidad'));
    if (targets.length === 0) {
      setShowSticky(true);
      return;
    }
    const visible = new Map<Element, boolean>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) visible.set(entry.target, entry.isIntersecting);
      setShowSticky(!Array.from(visible.values()).some(Boolean));
    });
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  const onWhatsAppClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!whatsappLink) return;
      event.preventDefault();
      openTrackedWhatsAppHref(whatsappLink, { cta_position: whatsappPosition });
    },
    [whatsappLink, whatsappPosition]
  );

  const onPrimaryClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!primaryHref.startsWith('#')) return;

      const targetId = primaryHref.slice(1);
      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();

      const nextUrl = new URL(window.location.href);
      nextUrl.hash = targetId;
      window.history.pushState(null, '', nextUrl);

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('pacartagena:open-availability-datepicker', {
            detail: { targetId },
          })
        );
      }, 220);
    },
    [primaryHref]
  );

  return (
    <div
      aria-label="Acciones rápidas"
      className={`${showSticky || intakeOpen ? '' : 'hidden'} md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-3 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-[0_-2px_10px_rgba(0,0,0,0.06)]`}
    >
      {/* Brief 2026-05-18 P0: alinear con sticky de ficha. Disponibilidad primary
          sólida + WhatsApp outline (no dos botones llenos compitiendo). */}
      <div className="grid grid-cols-2 gap-2">
        <a
          href={primaryHref}
          onClick={onPrimaryClick}
          className="min-h-[44px] inline-flex items-center justify-center bg-gray-900 hover:-translate-y-px active:translate-y-0 transition-transform text-white font-semibold text-[13px] px-3 py-2 rounded-lg"
        >
          {primaryText}
        </a>
        {intake ? (
          // Asistente de fechas (W2): abre el intake; cta_click_whatsapp lo
          // emite el sheet al salir hacia WhatsApp, no este tap.
          <button
            type="button"
            onClick={() => setIntakeOpen(true)}
            data-cta-position={whatsappPosition}
            className="min-h-[44px] inline-flex items-center justify-center gap-1.5 bg-white border border-wsp-deep/40 text-wsp-deep hover:bg-wsp-deep/5 hover:border-wsp-deep/60 active:bg-wsp-deep/10 transition-colors font-semibold text-[13px] px-3 py-2 rounded-lg"
            aria-label="Hablar por WhatsApp"
          >
            <WhatsAppGlyph size={16} />
            <span>{whatsappText}</span>
          </button>
        ) : whatsappLink ? (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onWhatsAppClick}
            data-cta-position={whatsappPosition}
            className="min-h-[44px] inline-flex items-center justify-center gap-1.5 bg-white border border-wsp-deep/40 text-wsp-deep hover:bg-wsp-deep/5 hover:border-wsp-deep/60 active:bg-wsp-deep/10 transition-colors font-semibold text-[13px] px-3 py-2 rounded-lg"
            aria-label="Hablar por WhatsApp"
          >
            <WhatsAppGlyph size={16} />
            <span>{whatsappText}</span>
          </a>
        ) : null}
      </div>
      {intake && (
        <WhatsAppIntakeSheet
          open={intakeOpen}
          onClose={() => setIntakeOpen(false)}
          context={{ ...intake, ctaPosition: `intake_${whatsappPosition}` }}
        />
      )}
    </div>
  );
}
